import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { axiosPost } from "api";
import StockUnitSelectionModal from "components/Operations/StockUnitSelectionModal";
import ObservationRecordsModal, {
  ObservationRecordSavePayload,
} from "components/utils/ObservationRecordsModal";
import {
  MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL,
  MOVEMENT_ARTICLES_GROUPED_OUTCOME_STOCK_UNIT_SELECTION_POOL_URL,
} from "constants/urls";
import { formatDateOnlyDMY } from "helpers";
import {
  buildLocationsById,
  buildParentAndSubLocation,
} from "helpers/articleLocationSelector";
import {
  buildObservationPreviewRecords,
  buildObservationPreviewUnitsFromSelectionPool,
  buildSelectionPoolFromPreviewUnits,
  buildStockUnitSelection,
  hasMultipleObservationValues,
  normalizeObservationInput,
  ObservationPreviewUnit,
} from "helpers/stockUnitObservationSelection";
import { useToast } from "hooks/useToast";
import { MdAllInbox, MdBlock, MdVisibility } from "react-icons/md";
import { RxPlus } from "react-icons/rx";
import {
  GroupedOutcomeObservationPreviewPayload,
  GroupedOutcomeObservationPreviewStockUnit,
  GroupedOutcomeStockUnitSelectionPoolPayload,
  GroupedOutcomeStockUnitSelectionPoolResponse,
  GroupedOutcomeStockUnitSelectionPoolStockUnit,
  OutcomeMovementArticleRowProps,
} from "types";

export default function AddMovementArticleRow({
  movement,
  groupedMovementArticle,
  rowIndex,
  onMovementArticleAdd,
  showLote,
  showOC,
  showFechaVencimiento,
  locations,
}: OutcomeMovementArticleRowProps) {
  const [amount, setAmount] = useState<number>(
    Math.max(0, groupedMovementArticle.available_amount),
  );
  const [observationPreviewUnits, setObservationPreviewUnits] = useState<
    ObservationPreviewUnit[]
  >([]);
  const [observationRecordsOpen, setObservationRecordsOpen] =
    useState<boolean>(false);
  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState<boolean>(false);
  const [stockUnitSelectionPoolOpen, setStockUnitSelectionPoolOpen] =
    useState<boolean>(false);
  const [stockUnitSelectionPoolLoading, setStockUnitSelectionPoolLoading] =
    useState<boolean>(false);
  const [stockUnitSelectionPool, setStockUnitSelectionPool] = useState<
    GroupedOutcomeStockUnitSelectionPoolStockUnit[]
  >([]);
  const { showSnackBar } = useToast();

  const articleLocation = groupedMovementArticle.article_locations;

  const locationsById = useMemo(() => {
    return buildLocationsById(locations);
  }, [locations]);

  const parentAndSubLocation = useMemo(() => {
    return buildParentAndSubLocation(articleLocation.location, locationsById);
  }, [articleLocation.location, locationsById]);

  const stockUsedInThisMovement = useMemo(
    () =>
      movement.articles
        .filter(
          (article) =>
            article.group_key === groupedMovementArticle.group_key &&
            !article.is_reserved,
        )
        .reduce((sum, article) => sum + article.amount, 0),
    [groupedMovementArticle.group_key, movement.articles],
  );

  const reservedUsedInThisMovement = useMemo(
    () =>
      movement.articles
        .filter(
          (article) =>
            article.group_key === groupedMovementArticle.group_key &&
            article.is_reserved,
        )
        .reduce((sum, article) => sum + article.amount, 0),
    [groupedMovementArticle.group_key, movement.articles],
  );

  const effectiveReserved = useMemo(
    () =>
      Math.max(0, groupedMovementArticle.reserved_total - reservedUsedInThisMovement),
    [groupedMovementArticle.reserved_total, reservedUsedInThisMovement],
  );

  const effectiveNotAvailable = useMemo(
    () => Math.max(0, groupedMovementArticle.not_available_total),
    [groupedMovementArticle.not_available_total],
  );
  const inWorkOrders = Math.max(0, Number(groupedMovementArticle.in_work_orders_total ?? 0));

  const disponible = useMemo(
    () =>
      Math.max(
        0,
        groupedMovementArticle.available_amount - stockUsedInThisMovement,
      ),
    [groupedMovementArticle.available_amount, stockUsedInThisMovement],
  );

  const alreadyAdded = useMemo(
    () =>
      movement.articles.some(
        (article) => article.group_key === groupedMovementArticle.group_key,
      ),
    [groupedMovementArticle.group_key, movement.articles],
  );

  const amountIsInvalid = amount > disponible || amount <= 0;
  const disableAddButton =
    amountIsInvalid || alreadyAdded || stockUnitSelectionPoolLoading;

  const observationPreviewRecords = useMemo(
    () => buildObservationPreviewRecords(observationPreviewUnits),
    [observationPreviewUnits],
  );

  const stockUnitSelection = useMemo(
    () =>
      observationPreviewUnits.length > 0
        ? buildStockUnitSelection(observationPreviewUnits)
        : null,
    [observationPreviewUnits],
  );

  const hasObservationOverrides =
    (stockUnitSelection?.observation_overrides.length ?? 0) > 0;

  const resetObservationPreview = () => {
    setObservationPreviewUnits([]);
    setObservationRecordsOpen(false);
    setObservationRecordsLoading(false);
    setStockUnitSelectionPool([]);
    setStockUnitSelectionPoolOpen(false);
    setStockUnitSelectionPoolLoading(false);
  };

  const buildMovementArticleFromPreviewUnits = (
    previewUnits: ObservationPreviewUnit[],
  ) => {
    const previewStockUnitSelection =
      previewUnits.length > 0 ? buildStockUnitSelection(previewUnits) : null;

    onMovementArticleAdd({
      id: rowIndex,
      group_key: groupedMovementArticle.group_key,
      observation_override: false,
      stock_unit_selection: previewStockUnitSelection ?? undefined,
      observation_preview_units: previewUnits,
      article_locations: articleLocation,
      article_locations_id: groupedMovementArticle.article_location_id,
      article_id: articleLocation.article.id,
      client_id: articleLocation.article.client.id,
      location_id: articleLocation.location.id,
      container: articleLocation.article.container.description,
      container_id: articleLocation.article.container.id,
      unit_id: articleLocation.article.unit.id,
      description: articleLocation.article.description,
      weight: articleLocation.article.weight,
      volume_weight: articleLocation.article.container?.volume_weight ?? 0,
      unit: articleLocation.article.unit.description,
      presentation: articleLocation.article.presentation ?? null,
      amount,
      amount_per_container: articleLocation.amount_per_container ?? 0,
      lote: articleLocation.lote ?? undefined,
      oc: articleLocation.oc ?? undefined,
      fecha_ingreso: articleLocation.fecha_ingreso ?? null,
      fecha_vencimiento: articleLocation.fecha_vencimiento ?? undefined,
      subtotal: amount * (articleLocation.amount_per_container ?? 0),
      subtotal_kg: amount * articleLocation.article.weight,
    });

    setAmount(1);
  };

  const loadObservationPreviewUnits = async (): Promise<
    ObservationPreviewUnit[] | null
  > => {
    if (amountIsInvalid) {
      showSnackBar(
        `Selecciona una cantidad valida para revisar unidades. Maximo disponible: ${disponible}.`,
        "error",
      );
      return null;
    }

    if (observationPreviewUnits.length > 0) {
      return observationPreviewUnits;
    }

    setObservationRecordsLoading(true);

    const response = await axiosPost<GroupedOutcomeObservationPreviewPayload>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL,
      {
        client_id: movement.client_id,
        article_location_ids: groupedMovementArticle.article_location_ids,
        amount,
      },
      true,
    );

    setObservationRecordsLoading(false);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ??
          "No se pudieron obtener las unidades a egresar.",
        "error",
      );
      return null;
    }

    const previewUnits = (
      (response.data ?? []) as GroupedOutcomeObservationPreviewStockUnit[]
    ).map((previewUnit) => ({
      ...previewUnit,
      original_observation: previewUnit.observation ?? null,
    }));

    setObservationPreviewUnits(previewUnits);
    setStockUnitSelectionPool(buildSelectionPoolFromPreviewUnits(previewUnits));
    return previewUnits;
  };

  const loadStockUnitSelectionPool = async (): Promise<
    GroupedOutcomeStockUnitSelectionPoolStockUnit[] | null
  > => {
    if (amountIsInvalid) {
      showSnackBar(
        `Selecciona una cantidad valida para elegir unidades. Maximo disponible: ${disponible}.`,
        "error",
      );
      return null;
    }

    if (stockUnitSelectionPool.length > 0) {
      return stockUnitSelectionPool;
    }

    setStockUnitSelectionPoolLoading(true);

    const response = await axiosPost<GroupedOutcomeStockUnitSelectionPoolPayload>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_STOCK_UNIT_SELECTION_POOL_URL,
      {
        client_id: movement.client_id,
        article_location_ids: groupedMovementArticle.article_location_ids,
        amount,
      },
      true,
    );

    setStockUnitSelectionPoolLoading(false);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ??
          "No se pudo obtener el pool de observaciones para egresar.",
        "error",
      );
      return null;
    }

    const nextStockUnitSelectionPool =
      (response.data as GroupedOutcomeStockUnitSelectionPoolResponse | undefined)
        ?.stock_units ?? [];

    setStockUnitSelectionPool(nextStockUnitSelectionPool);
    return nextStockUnitSelectionPool;
  };

  const handleSubmit = async () => {
    if (amount > disponible) {
      showSnackBar(
        `La cantidad a egresar no puede ser mayor al disponible (${disponible})`,
        "error",
      );
      return;
    }

    const selectableStockUnits =
      stockUnitSelectionPool.length > 0
        ? stockUnitSelectionPool
        : await loadStockUnitSelectionPool();

    if (!selectableStockUnits) {
      return;
    }

    if (hasMultipleObservationValues(selectableStockUnits)) {
      setStockUnitSelectionPoolOpen(true);
      return;
    }

    const automaticPreviewSource =
      observationPreviewUnits.length > 0
        ? observationPreviewUnits
        : buildObservationPreviewUnitsFromSelectionPool(selectableStockUnits);

    buildMovementArticleFromPreviewUnits(
      automaticPreviewSource.slice(0, amount),
    );
  };

  const handleOpenObservationRecords = async () => {
    const previewUnits = await loadObservationPreviewUnits();

    if (!previewUnits) {
      return;
    }

    setObservationRecordsOpen(true);
  };

  const handleSaveObservationRecord = async ({
    recordId,
    observation,
    quantityMode,
    specificQuantity,
  }: ObservationRecordSavePayload) => {
    const targetRecord = observationPreviewRecords.find(
      (record) => record.id === recordId,
    );

    if (!targetRecord) {
      showSnackBar(
        "No se encontro el grupo de observaciones seleccionado.",
        "error",
      );
      return;
    }

    const targetQuantity =
      quantityMode === "specific"
        ? Number(specificQuantity ?? 0)
        : targetRecord.stock_unit_ids.length;

    if (
      !Number.isFinite(targetQuantity) ||
      targetQuantity <= 0 ||
      targetQuantity > targetRecord.stock_unit_ids.length
    ) {
      showSnackBar("La cantidad especifica seleccionada no es valida.", "error");
      return;
    }

    const nextObservation = normalizeObservationInput(observation);
    const affectedStockUnitIds = new Set(
      targetRecord.stock_unit_ids.slice(0, targetQuantity),
    );

    setObservationPreviewUnits((currentPreviewUnits) =>
      currentPreviewUnits.map((previewUnit) =>
        affectedStockUnitIds.has(previewUnit.id)
          ? {
              ...previewUnit,
              observation: nextObservation,
            }
          : previewUnit,
      ),
    );

    setStockUnitSelectionPool((currentStockUnitSelectionPool) =>
      currentStockUnitSelectionPool.map((stockUnit) =>
        affectedStockUnitIds.has(stockUnit.id)
          ? {
              ...stockUnit,
              observation: nextObservation,
            }
          : stockUnit,
      ),
    );
  };

  useEffect(() => {
    resetObservationPreview();
  }, [amount, groupedMovementArticle.group_key]);

  useEffect(() => {
    setAmount(Math.max(0, disponible));
  }, [groupedMovementArticle.group_key, disponible]);

  return (
    <>
    <TableRow>
      <TableCell align="center">{articleLocation.article.code}</TableCell>
      <TableCell align="center">{articleLocation.article.description}</TableCell>
      <TableCell align="center">
        {articleLocation.article.units_per_pallet}
      </TableCell>

      <TableCell align="center">
        <TextField
          data-cy={`qty-${articleLocation.article.code}`}
          label="Cantidad a egresar"
          type="number"
          value={amount}
          onChange={(event) =>
            setAmount(Math.max(0, Number(event.target.value) || 0))
          }
          error={amountIsInvalid}
          helperText={amountIsInvalid ? `Maximo ${disponible}` : undefined}
          inputProps={{ min: 0, max: disponible }}
        />
      </TableCell>

        <TableCell align="center">
          {articleLocation.amount_per_container ?? "-"}
        </TableCell>

        {showLote && (
          <TableCell align="center">{articleLocation.lote || "-"}</TableCell>
        )}

        <TableCell align="center">
          {articleLocation.fecha_ingreso
            ? formatDateOnlyDMY(articleLocation.fecha_ingreso)
            : "-"}
        </TableCell>

        <TableCell align="center">{parentAndSubLocation.parent}</TableCell>
        <TableCell align="center">{parentAndSubLocation.sub || "-"}</TableCell>

        {showOC && <TableCell align="center">{articleLocation.oc || "-"}</TableCell>}

        {showFechaVencimiento && (
          <TableCell align="center">
            {articleLocation.fecha_vencimiento
              ? formatDateOnlyDMY(articleLocation.fecha_vencimiento)
              : "-"}
          </TableCell>
        )}

        <TableCell align="center">
          <Tooltip
            title={
              amountIsInvalid
                ? "Selecciona primero una cantidad valida para ver las observaciones."
                : hasObservationOverrides
                  ? "Editar observaciones seleccionadas"
                  : "Ver observaciones de las unidades a egresar"
            }
          >
            <span>
              <IconButton
                color={hasObservationOverrides ? "primary" : "default"}
                disabled={amountIsInvalid || observationRecordsLoading}
                onClick={() => void handleOpenObservationRecords()}
              >
                {observationRecordsLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <MdVisibility />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>

        <TableCell align="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography noWrap>{disponible}</Typography>
            <Typography noWrap display="flex" alignItems="center" gap={0.4}>
              ({effectiveReserved} <MdAllInbox />)
            </Typography>
            <Typography
              noWrap
              display="flex"
              alignItems="center"
              gap={0.4}
              color="error.main"
            >
              ({effectiveNotAvailable} <MdBlock />)
            </Typography>
            <Typography noWrap>
              ({inWorkOrders} En OT)
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="center">
          <Tooltip
            disableHoverListener={!disableAddButton}
            title={
              alreadyAdded
                ? "Esta linea agrupada ya fue agregada al movimiento"
                : amountIsInvalid
                  ? "La cantidad seleccionada supera el stock disponible"
                  : stockUnitSelectionPoolLoading
                    ? "Cargando unidades disponibles"
                  : ""
            }
          >
            <Box>
              <Button
                data-cy={`add-mov-art-${articleLocation.article.code}`}
                variant="contained"
                type="button"
                disabled={disableAddButton}
                onClick={() => void handleSubmit()}
              >
                <RxPlus size="1.2rem" />
              </Button>
            </Box>
          </Tooltip>
        </TableCell>
      </TableRow>

      <ObservationRecordsModal
        open={observationRecordsOpen}
        loading={observationRecordsLoading}
        title="Observaciones de las unidades a egresar"
        subtitle={`${articleLocation.article.code} - ${articleLocation.article.description} | Cantidad a egresar: ${amount} | Pool elegible completo`}
        records={observationPreviewRecords}
        onClose={() => setObservationRecordsOpen(false)}
        onSaveRecord={handleSaveObservationRecord}
      />

      <StockUnitSelectionModal
        open={stockUnitSelectionPoolOpen}
        amount={amount}
        stockUnits={stockUnitSelectionPool}
        title="Seleccionar unidades a egresar"
        subtitle={`${articleLocation.article.code} - ${articleLocation.article.description} | Cantidad seleccionada: ${amount}`}
        onClose={() => setStockUnitSelectionPoolOpen(false)}
        onConfirm={(selectedStockUnitIds) => {
          const selectedPreviewUnits = (
            observationPreviewUnits.length > 0
              ? observationPreviewUnits
              : buildObservationPreviewUnitsFromSelectionPool(
                  stockUnitSelectionPool,
                )
          ).filter((previewUnit) => selectedStockUnitIds.includes(previewUnit.id));

          setObservationPreviewUnits(selectedPreviewUnits);
          setStockUnitSelectionPoolOpen(false);
          buildMovementArticleFromPreviewUnits(selectedPreviewUnits);
        }}
      />
    </>
  );
}
